import React, { useState, useEffect, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Video, ArrowRight, Loader2, Calendar, ExternalLink, AlertCircle, Timer, PenLine } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter
} from "@/components/ui/dialog";
import { VirtualClass } from "./VirtualClasses";

import { API_URL } from "@/config";

const PublicVirtualClassAccess: React.FC = () => {
    const [email, setEmail] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [classes, setClasses] = useState<VirtualClass[]>([]);
    const [errorJson, setErrorJson] = useState<any>(null);
    const [searchParams] = useSearchParams();
    const [isAutoChecking, setIsAutoChecking] = useState(false);
    const [showRecordingModal, setShowRecordingModal] = useState(false);

    const [countdown, setCountdown] = useState<string | null>(null);
    const [signaturePolling, setSignaturePolling] = useState(false);
    const pollingInFlight = useRef(false);

    const classId = searchParams.get('c');
    const emailParam = searchParams.get('e');

    // Automatic check if email is provided in URL
    useEffect(() => {
        if (emailParam && classId && !isSuccess && !isSubmitting && !errorJson) {
            setEmail(emailParam);
            handleVerifyAccess(emailParam);
        }
    }, [emailParam, classId]);

    // Countdown logic
    useEffect(() => {
        if (errorJson?.error === 'too_early' && errorJson?.next_retry) {
            const timer = setInterval(() => {
                const now = new Date().getTime();
                const target = new Date(errorJson.next_retry).getTime();
                const diff = target - now;

                if (diff <= 0) {
                    setCountdown(null);
                    clearInterval(timer);
                    // Automatically retry or refresh when countdown hits zero
                    handleVerifyAccess(email);
                    return;
                }

                const days = Math.floor(diff / (1000 * 60 * 60 * 24));
                const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((diff % (1000 * 60)) / 1000);

                let parts = [];
                if (days > 0) {
                    parts.push(`${days}j`);
                    parts.push(`${hours}h`);
                    parts.push(`${minutes}m`);
                } else if (hours > 0) {
                    parts.push(`${hours}h`);
                    parts.push(`${minutes}m`);
                    parts.push(`${seconds}s`);
                } else {
                    parts.push(`${minutes}m`);
                    parts.push(`${seconds}s`);
                }

                setCountdown(parts.join(' '));
            }, 1000);

            return () => clearInterval(timer);
        } else {
            setCountdown(null);
        }
    }, [errorJson, email]);

    // Poll for signature completion every 10 seconds
    useEffect(() => {
        if (!signaturePolling || !email || !classId) return;

        const pollInterval = setInterval(async () => {
            if (pollingInFlight.current) return;
            pollingInFlight.current = true;
            try {
                await handleVerifyAccess(email, true);
            } finally {
                pollingInFlight.current = false;
            }
        }, 10000);

        return () => clearInterval(pollInterval);
    }, [signaturePolling, email, classId]);

    // Start polling when signature_required error is received
    useEffect(() => {
        if (errorJson?.error === 'signature_required') {
            setSignaturePolling(true);
        } else {
            setSignaturePolling(false);
        }
    }, [errorJson?.error]);

    const handleVerifyAccess = async (emailToVerify: string, silent = false) => {
        if (!emailToVerify.trim()) return;

        const isAuto = !silent && emailParam === emailToVerify;
        if (isAuto) setIsAutoChecking(true);
        if (!silent) {
            setIsSubmitting(true);
            setErrorJson(null);
        }

        try {
            const response = await fetch(`${API_URL}/check-class-access`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify({ email: emailToVerify, class_id: classId, ...(silent && { polling: true }) }),
            });

            const contentType = response.headers.get("content-type");
            if (!contentType || !contentType.includes("application/json")) {
                const text = await response.text();
                console.error("Non-JSON response received:", text);
                throw new Error("Le serveur a renvoyé une réponse invalide (non-JSON).");
            }

            const data = await response.json();

            if (response.ok) {
                const mappedClasses: VirtualClass[] = data.map((item: any) => ({
                    ...item,
                    startTime: item.start_time,
                    endTime: item.end_time,
                    createdAt: item.created_at || new Date().toISOString(),
                    createdBy: item.created_by || 'Unknown'
                }));

                setClasses(mappedClasses);

                // If we have a class with a URL, redirect or show success state
                if (mappedClasses.length > 0 && mappedClasses[0].url) {
                    setIsSuccess(true);
                    toast.success("Accès autorisé");

                    // Instead of immediate redirect, show the recording modal
                    setTimeout(() => {
                        setShowRecordingModal(true);
                    }, 500);
                } else {
                    setIsSuccess(true);
                    toast.success("Accès autorisé");
                }
            } else {
                if (!silent) {
                    setErrorJson(data);
                    if (!isAuto) toast.error("Accès refusé");
                } else if (data?.error !== 'signature_required') {
                    // During polling, only update if status changed from signature_required
                    setErrorJson(data);
                    setSignaturePolling(false);
                }
            }
        } catch (error: any) {
            console.error("API Error:", error);
            if (!silent) {
                setErrorJson({ error: 'api_error', message: error.message || "Une erreur est survenue lors de la connexion au serveur." });
                if (!isAuto) toast.error("Une erreur est survenue");
            }
        } finally {
            if (!silent) {
                setIsSubmitting(false);
                setIsAutoChecking(false);
            }
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        handleVerifyAccess(email);
    };

    if (!classId) {
        return (
            <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
                <Card className="w-full max-w-md border-slate-200 shadow-xl bg-white/80 backdrop-blur-sm text-center p-8">
                    <div className="h-16 w-16 bg-red-50 text-red-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                        <AlertCircle className="h-8 w-8" />
                    </div>
                    <h1 className="text-2xl font-bold text-slate-900 mb-2">Lien incorrect</h1>
                    <p className="text-slate-500 mb-8">Ce lien d'accès à la classe virtuelle est invalide ou expiré.</p>
                    <Link to="/">
                        <Button className="w-full font-bold">Retour à l'accueil</Button>
                    </Link>
                </Card>
            </div>
        );
    }

    if (isAutoChecking) {
        return (
            <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
                <div className="text-center space-y-4">
                    <div className="flex justify-center">
                        <div className="h-16 w-16 rounded-full border-4 border-slate-200 border-t-primary animate-spin" />
                    </div>
                    <div>
                        <h2 className="text-xl font-bold text-slate-900">Vérification de vos accès</h2>
                        <p className="text-sm text-slate-500 font-medium">Veuillez patienter quelques instants...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (isSuccess) {
        const targetClass = classes[0];

        const handleConfirmRecording = () => {
            if (targetClass?.url) {
                window.location.href = targetClass.url;
            }
        };

        return (
            <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
                <div className="text-center space-y-6 max-w-md animate-in fade-in zoom-in duration-500">
                    <div className="relative mx-auto w-20 h-20">
                        <div className="absolute inset-0 bg-primary/20 rounded-full animate-ping" />
                        <div className="relative h-20 w-20 bg-primary text-primary-foreground rounded-full flex items-center justify-center shadow-2xl shadow-primary/40">
                            <Video className="h-10 w-10" />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <h1 className="text-2xl font-black text-slate-900">Accès autorisé</h1>
                        <p className="text-slate-500 font-medium">
                            Vous êtes sur le point de rejoindre la classe virtuelle <span className="text-primary">"{targetClass?.name}"</span>.
                        </p>
                    </div>

                    <div className="p-4 bg-white rounded-2xl border border-slate-200 shadow-sm flex items-center gap-3">
                        <Avatar className="h-10 w-10 ring-2 ring-slate-50">
                            <AvatarFallback className="bg-slate-100 text-slate-600 font-bold">
                                {targetClass?.createdBy?.charAt(0) || "H"}
                            </AvatarFallback>
                        </Avatar>
                        <div className="text-left">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Organisé par</p>
                            <p className="text-sm font-bold text-slate-700">{targetClass?.createdBy || "AlterMedic Center"}</p>
                        </div>
                    </div>

                    <Button
                        onClick={() => setShowRecordingModal(true)}
                        className="w-full h-12 font-bold text-lg shadow-xl shadow-primary/20 group"
                    >
                        Rejoindre la session
                        <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    </Button>
                </div>

                <Dialog
                    open={showRecordingModal}
                    onOpenChange={(open) => {
                        // Prevent closing the modal unless the button is clicked
                        if (!open) return;
                        setShowRecordingModal(open);
                    }}
                >
                    <DialogContent
                        className="sm:max-w-[425px] overflow-hidden border-none p-0 bg-white"
                        onPointerDownOutside={(e) => e.preventDefault()}
                        onEscapeKeyDown={(e) => e.preventDefault()}
                        showCloseButton={false}
                    >
                        <div className="relative">
                            {/* Decorative Header */}
                            <div className="h-24 bg-primary/10 flex items-center justify-center">
                                <div className="h-12 w-12 rounded-full bg-white flex items-center justify-center shadow-sm text-primary">
                                    <Video className="h-6 w-6" />
                                </div>
                            </div>

                            <div className="p-6 pt-8 text-center space-y-4">
                                <DialogHeader>
                                    <DialogTitle className="text-2xl font-black text-slate-900 text-center">
                                        Information Importante
                                    </DialogTitle>
                                    <DialogDescription className="text-slate-500 text-base font-medium pt-2 text-center">
                                        Veuillez noter que cette session sera <span className="text-primary font-bold">enregistrée</span> à des fins pédagogiques et de suivi qualité.
                                    </DialogDescription>
                                </DialogHeader>

                                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 text-left">
                                    <p className="text-sm text-slate-600 leading-relaxed italic">
                                        "En cliquant sur continuer, vous reconnaissez avoir été informé que le contenu audio et vidéo de cette séance sera enregistré et vous autorisez AlterMedic Center à enregistrer et à utiliser ce contenu pour des fins pédagogiques et de suivi qualité."
                                    </p>
                                </div>

                                <DialogFooter className="pt-4">
                                    <Button
                                        onClick={handleConfirmRecording}
                                        className="w-full h-12 font-bold text-base shadow-lg shadow-primary/20"
                                    >
                                        J'ai compris, continuer
                                    </Button>
                                </DialogFooter>
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>
        );
    }

    // Signature required: two-column layout
    if (errorJson?.error === 'signature_required') {
        return (
            <div className="min-h-screen bg-slate-50 flex flex-col relative overflow-hidden">
                {/* Background elements */}
                <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                    <div className="absolute top-[-10%] right-[-5%] w-96 h-96 bg-primary/5 rounded-full blur-3xl opacity-50" />
                    <div className="absolute bottom-[-10%] left-[-5%] w-96 h-96 bg-blue-500/5 rounded-full blur-3xl opacity-50" />
                </div>

                <div className="flex-1 z-10 flex flex-col lg:flex-row w-full animate-in fade-in duration-500">
                    {/* Left column: Info & actions */}
                    <div className="lg:w-[380px] xl:w-[420px] shrink-0 flex flex-col justify-center items-center p-6 lg:p-10">
                        <div className="mb-8 text-center">
                            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary text-primary-foreground mb-4 shadow-lg shadow-primary/20">
                                <Video className="h-6 w-6" />
                            </div>
                            <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 mb-2">
                                Classe Virtuelle
                            </h1>
                            <p className="text-slate-500 font-medium text-sm">
                                Signez la fiche de présence pour accéder à la session.
                            </p>
                        </div>

                        <div className="space-y-4 w-full">
                            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
                                <p className="font-bold mb-2 flex items-center gap-2">
                                    <PenLine className="h-4 w-4 text-amber-600" />
                                    Fiche de présence requise
                                </p>
                                {errorJson.signature_link ? (
                                    <p>Veuillez signer la fiche de présence ci-contre pour accéder à la classe virtuelle.</p>
                                ) : (
                                    <p>Impossible de charger le formulaire de signature. Un email vous sera envoyé par <span className="font-bold">EduSign</span>.</p>
                                )}
                            </div>

                            <div className="flex items-center gap-2 p-3 bg-white border border-slate-200 rounded-lg">
                                <div className="h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
                                <p className="text-xs text-slate-500 font-medium">
                                    Vérification automatique en cours...
                                </p>
                            </div>

                            <Button
                                onClick={() => handleVerifyAccess(email)}
                                disabled={isSubmitting}
                                className="w-full bg-amber-600 hover:bg-amber-700 text-white font-bold"
                            >
                                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <PenLine className="h-4 w-4 mr-2" />}
                                J'ai signé, vérifier
                            </Button>

                            <Button
                                onClick={() => setErrorJson(null)}
                                variant="outline"
                                className="w-full border-slate-200 hover:bg-slate-50"
                            >
                                Fermer et réessayer
                            </Button>
                        </div>

                        <p className="mt-8 text-center text-xs font-semibold text-slate-400 uppercase tracking-widest">
                            AMC Lead Dispatch V2.1
                        </p>
                    </div>

                    {/* Right column: Signature iframe */}
                    {errorJson.signature_link && (
                        <div className="flex-1 p-4 lg:p-6 flex flex-col min-h-[600px] lg:min-h-0">
                            <div className="relative flex-1 w-full rounded-xl overflow-hidden border border-slate-200 bg-white shadow-lg">
                                <iframe
                                    src={errorJson.signature_link}
                                    className="absolute inset-0 w-full h-full"
                                    style={{ border: 'none' }}
                                    title="EduSign - Signature de présence"
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4 relative overflow-hidden">
            {/* Background elements */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                <div className="absolute top-[-10%] right-[-5%] w-96 h-96 bg-primary/5 rounded-full blur-3xl opacity-50" />
                <div className="absolute bottom-[-10%] left-[-5%] w-96 h-96 bg-blue-500/5 rounded-full blur-3xl opacity-50" />
            </div>

            <div className="w-full max-w-md z-10 animate-in fade-in slide-in-from-bottom-4 duration-700">
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary text-primary-foreground mb-4 shadow-lg shadow-primary/20">
                        <Video className="h-6 w-6" />
                    </div>
                    <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 mb-2">
                        Classe Virtuelle
                    </h1>
                    <p className="text-slate-500 font-medium">
                        Veuillez vous identifier pour accéder à la session.
                    </p>
                </div>

                <Card className="border-slate-200 shadow-xl bg-white/80 backdrop-blur-sm">
                    <CardHeader className="space-y-1 pb-2">
                        <CardTitle className="text-lg font-bold">Connexion Invité</CardTitle>
                        <CardDescription>Entrez votre adresse email utilisée pour accéder aux formations.</CardDescription>
                    </CardHeader>
                    <CardContent>
                        {errorJson ? (
                            <div className="space-y-6 animate-in fade-in zoom-in duration-300">

                                {/* Registration Expired */}
                                {errorJson.error === 'registration_expired' && (
                                    <div className="space-y-4">
                                        <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg text-orange-700 text-sm">
                                            <p className="font-bold mb-2 flex items-center gap-2">
                                                <span className="h-2 w-2 rounded-full bg-orange-500" />
                                                Inscription expirée
                                            </p>
                                            <p>Votre inscription a expiré. Veuillez vous inscrire pour le suivi continu ici.</p>
                                        </div>
                                        <Button asChild className="w-full bg-orange-600 hover:bg-orange-700 text-white font-bold">
                                            <a href="#" target="_blank" rel="noopener noreferrer">
                                                Renouveler mon inscription <ExternalLink className="ml-2 h-4 w-4" />
                                            </a>
                                        </Button>
                                    </div>
                                )}


                                {/* Not Student */}
                                {errorJson.error === 'not_student' && (
                                    <div className="space-y-4">
                                        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-sm">
                                            <p className="font-bold mb-2 flex items-center gap-2">
                                                <span className="h-2 w-2 rounded-full bg-blue-500" />
                                                Non inscrit
                                            </p>
                                            <p>Vous n'êtes pas inscrit aux cours. Vous souhaitez accéder à la classe virtuelle ? Inscrivez-vous ici.</p>
                                        </div>
                                        <Button asChild className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold">
                                            <a href="#" target="_blank" rel="noopener noreferrer">
                                                M'inscrire maintenant <ExternalLink className="ml-2 h-4 w-4" />
                                            </a>
                                        </Button>
                                    </div>
                                )}

                                {/* Too Early */}
                                {errorJson.error === 'too_early' && (
                                    <div className="space-y-4">
                                        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800 text-sm">
                                            <p className="font-bold mb-2 flex items-center gap-2">
                                                <span className="h-2 w-2 rounded-full bg-yellow-500" />
                                                Trop tôt
                                            </p>
                                            <p>{errorJson.message}</p>

                                            <div className="mt-4 grid grid-cols-2 gap-2 border-t border-yellow-200 pt-4">
                                                {errorJson.next_retry && (
                                                    <div className="flex flex-col">
                                                        <span className="text-[10px] uppercase tracking-wider text-yellow-600 font-bold">Ouverture</span>
                                                        <p className="text-sm font-black flex items-center gap-1.5 text-yellow-900">
                                                            <Calendar className="h-3.5 w-3.5" />
                                                            {new Date(errorJson.next_retry).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                        </p>
                                                    </div>
                                                )}
                                                {countdown && (
                                                    <div className="flex flex-col">
                                                        <span className="text-[10px] uppercase tracking-wider text-yellow-600 font-bold">Attente</span>
                                                        <p className="text-sm font-black flex items-center gap-1.5 text-yellow-900">
                                                            <Timer className="h-3.5 w-3.5 animate-pulse" />
                                                            {countdown}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <Button
                                            onClick={() => handleVerifyAccess(email)}
                                            disabled={isSubmitting}
                                            className="w-full bg-yellow-600 hover:bg-yellow-700 text-white font-bold"
                                        >
                                            {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                                            Réessayer maintenant
                                        </Button>
                                    </div>
                                )}


                                {/* Other Errors */}
                                {!['registration_expired', 'not_student', 'too_early', 'signature_required'].includes(errorJson.error) && (
                                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm overflow-auto max-h-60">
                                        <p className="font-bold mb-2 flex items-center gap-2">
                                            <span className="h-2 w-2 rounded-full bg-red-500" />
                                            Accès refusé
                                        </p>
                                        <p className="text-sm text-slate-700">
                                            {errorJson.error === 'registration_suspended' && "Votre accès à la formation est suspendu."}
                                            {(!['registration_suspended'].includes(errorJson.error)) && (errorJson.message || "Une erreur est survenue.")}
                                        </p>
                                    </div>
                                )}

                                <Button
                                    onClick={() => setErrorJson(null)}
                                    variant="outline"
                                    className="w-full border-slate-200 hover:bg-slate-50"
                                >
                                    Fermer et réessayer
                                </Button>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="space-y-2">
                                    <label htmlFor="email" className="text-sm font-medium text-slate-700 sr-only">
                                        Email
                                    </label>
                                    <Input
                                        id="email"
                                        type="email"
                                        placeholder="nom@entreprise.com"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        className="h-11 bg-white"
                                        autoComplete="email"
                                        autoFocus
                                    />
                                </div>
                                <Button
                                    type="submit"
                                    className="w-full h-11 font-bold shadow-lg shadow-primary/20"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Vérification...
                                        </>
                                    ) : (
                                        <>
                                            Accéder à la classe <ArrowRight className="ml-2 h-4 w-4" />
                                        </>
                                    )}
                                </Button>
                            </form>
                        )}
                    </CardContent>
                </Card>

                <p className="mt-8 text-center text-xs font-semibold text-slate-400 uppercase tracking-widest">
                    AMC Lead Dispatch V2.1
                </p>
            </div>
        </div>
    );
};

export default PublicVirtualClassAccess;
